import {
  EligibleRegisterGroup,
  ExplicitlyRemovedGroupSession,
  RegisterCompositionGroup,
} from '@schooly/api';

import { mergeCoupledScopes } from './registerCoupling';
import { getGroupAvailability } from './registerEligibility';
import { getGroupIdsForScope } from './registerGroupIds';
import { RegisterGroupIds, RegisterPeriod, RegisterScope, RosterGroup } from '../types';

export const sortGroupsByName = <T extends { name: string }>(items: T[]): T[] =>
  [...items].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }),
  );

const compositionGroupToEligible = (group: RegisterCompositionGroup): EligibleRegisterGroup => ({
  group_id: group.group_id,
  name: group.name,
  group_type: group.group_type,
  subject: group.subject,
  age_group_entries: [],
  sessions: group.sessions.map((session) => ({
    session,
    scheduled: false,
    explicitly_removed: false,
  })),
  slots: group.periods.map((period_id) => ({
    period_id,
    scheduled: false,
    explicitly_removed: false,
  })),
});

export const withCompositionSnapshot = (
  eligibleGroups: EligibleRegisterGroup[],
  compositionGroups: RegisterCompositionGroup[],
): RosterGroup[] => {
  if (!compositionGroups.length) return eligibleGroups;

  const byId = new Map<string, RosterGroup>(eligibleGroups.map((group) => [group.group_id, group]));

  for (const group of compositionGroups) {
    byId.set(group.group_id, {
      ...(byId.get(group.group_id) ?? compositionGroupToEligible(group)),
      snapshot: {
        sessions: group.sessions,
        period_ids: group.periods,
        age_group_entries: group.age_group_entries,
      },
    });
  }

  return [...byId.values()];
};

type RemovalContext = {
  initialRosterIds: string[];
  isEdit: boolean;
};

const isWouldBeExplicitlyRemoved = (
  group: EligibleRegisterGroup,
  scope: RegisterScope,
  { initialRosterIds, isEdit }: RemovalContext,
): boolean => {
  const availability = getGroupAvailability(group, scope);
  if (!availability) return false;
  if (availability.explicitly_removed) return true;
  if (!availability.scheduled) return false;

  return isEdit ? initialRosterIds.includes(group.group_id) : true;
};

type SidebarSections = {
  available: EligibleRegisterGroup[];
  explicitlyRemoved: EligibleRegisterGroup[];
};

type BuildSidebarSectionsParams = {
  eligibleGroups: EligibleRegisterGroup[];
  scope: RegisterScope;
  localRosterIds: string[];
  initialRosterIds: string[];
  isEdit: boolean;
};

export const buildSidebarSections = ({
  eligibleGroups,
  scope,
  localRosterIds,
  initialRosterIds,
  isEdit,
}: BuildSidebarSectionsParams): SidebarSections => {
  const available: EligibleRegisterGroup[] = [];
  const explicitlyRemoved: EligibleRegisterGroup[] = [];

  for (const group of eligibleGroups) {
    if (localRosterIds.includes(group.group_id)) continue;

    if (isWouldBeExplicitlyRemoved(group, scope, { initialRosterIds, isEdit })) {
      explicitlyRemoved.push(group);
    } else if (getGroupAvailability(group, scope)) {
      available.push(group);
    }
  }

  return {
    available: sortGroupsByName(available),
    explicitlyRemoved: sortGroupsByName(explicitlyRemoved),
  };
};

const getRemovableScopes = (group: EligibleRegisterGroup): RegisterScope[] => [
  ...group.sessions.map(({ session }) => ({ session })),
  ...group.slots.map(({ period_id }) => ({ periodId: period_id })),
];

type BuildExplicitlyRemovedPayloadParams = {
  eligibleGroups: EligibleRegisterGroup[];
  selectedGroupIds: RegisterGroupIds;
  initialRosterIds: RegisterGroupIds;
  isEdit: boolean;
  periods: RegisterPeriod[];
};

export const buildExplicitlyRemovedPayload = ({
  eligibleGroups,
  selectedGroupIds,
  initialRosterIds,
  isEdit,
  periods,
}: BuildExplicitlyRemovedPayloadParams): ExplicitlyRemovedGroupSession[] =>
  eligibleGroups.flatMap((group) => {
    const removedScopes = getRemovableScopes(group)
      .filter((scope) => !getGroupIdsForScope(selectedGroupIds, scope).includes(group.group_id))
      .filter((scope) =>
        isWouldBeExplicitlyRemoved(group, scope, {
          initialRosterIds: getGroupIdsForScope(initialRosterIds, scope),
          isEdit,
        }),
      );

    return mergeCoupledScopes(removedScopes, periods).map(({ session, periodId }) => ({
      session: session ?? null,
      group_id: group.group_id,
      period_id: periodId ?? null,
    }));
  });
